import {getFunctionName, uuid} from '../util/helpers';
import {Timer} from '../util/Timer';

const HOOK_NAME = {
    BEFORE_SEND: 'beforeSend',
    BEFORE_RESPONSE: 'beforeResponse',
    TRANSFORMERS: 'transformers',
    ERROR_HANDLERS: 'errorHandlers'
};

let _hooks = {
    [HOOK_NAME.BEFORE_SEND]: new Map(),
    [HOOK_NAME.BEFORE_RESPONSE]: new Map(),
    [HOOK_NAME.TRANSFORMERS]: new Map(),
    [HOOK_NAME.ERROR_HANDLERS]: new Map()
};

class ApiRequest {
    constructor({
                    contentType = 'application/json',
                    debug = true,
                    method = 'GET',
                    headers = {},
                    path = '',
                    longRunningRequestInterval = 5000,
                    processData = true
                    
    } = {}) {
        this.contentType = contentType;
        this.debug = debug;
        this.method = method;
        this.headers = headers;
        this.path = path;
        this.longRunningRequestInterval = longRunningRequestInterval;
        this.processData = processData;
    }

    static GET(args = {}, ...rest) {
        args.method = 'GET';
        return _customArgumentsApiRequestFactory(this, args, ...rest);
    }

    static POST(args = {}, ...rest) {
        args.method = 'POST';
        return _customArgumentsApiRequestFactory(this, args, ...rest);
    }

    static PUT(args = {}, ...rest) {
        args.method = 'PUT';
        return _customArgumentsApiRequestFactory(this, args, ...rest);
    }

    static DELETE(args = {}, ...rest) {
        args.method = 'DELETE';
        return _customArgumentsApiRequestFactory(this, args, ...rest);
    }

    static addBeforeSend(hook) {
        _addHook(hook, HOOK_NAME.BEFORE_SEND, this);
    }

    static addBeforeResponse(hook) {
        _addHook(hook, HOOK_NAME.BEFORE_RESPONSE, this);
    }

    static addTransformer(hook) {
        _addHook(hook, HOOK_NAME.TRANSFORMERS, this);
    }

    static addErrorHandler(hook) {
        _addHook(hook, HOOK_NAME.ERROR_HANDLERS, this);
    }

    // Iterates all beforeSend hooks passing reference to config object to be used in current request
    // Updates to reference will be reflected
    runBeforeSend(config) {
        _hooks[HOOK_NAME.BEFORE_SEND].forEach((hooks, context) => {
            if (this instanceof context) {
                hooks.forEach(hook => hook(config));
            }
        });
    }

    runHooks(collectionName, response) {
        let collection = _hooks[collectionName];

        collection.forEach((hooks, context) => {
            if (this instanceof context) {
                hooks.forEach(hook => {
                    response = hook(response);
                });
            }
        });

        return response;
    }

    setDebug(mode) {
        if(typeof(mode) !== 'boolean') {
            console.warn(`Attempting to change dev mode in ${this.constructor.name} with a non-bool value.`)
            return;
        }
        this.debug = mode;
    }

    handleBeforeRequestSend({requestId, payload, requestConfig, timer}) {
        if(this.debug && requestConfig.isDevMode) {
            console.log(`${this.constructor.name}:handleBeforeRequestSend\nId: ${requestId}\nUrl:${requestConfig.url}`);
        }
    }

    handleLongRunningRequestInterval({requestId, payload, requestConfig, timer}) {
        if(this.debug && requestConfig.isDevMode) {
            console.log(`${this.constructor.name}:handleLongRunningRequestInterval\nId: ${requestId}\nUrl:${requestConfig.url}\nRunning Time:${timer.runningTime()}ms`);
        }
    }

    handleRequestCompleted({requestId, payload, requestConfig, timer}) {
        if(this.debug && requestConfig.isDevMode) {
            console.log(`${this.constructor.name}:handleRequestCompleted\nId: ${requestId}\nUrl:${requestConfig.url}\nExecution Time:${timer.executionTime()}ms`);
        }
    }

    handleRequestError({requestId, error, payload, requestConfig, timer}) {
        if(this.debug && requestConfig.isDevMode) {
            console.log(`${this.constructor.name}:handleRequestError\nId: ${requestId}\nUrl:${requestConfig.url}\nExecution Time:${timer.executionTime()}ms`);
        }
    }

    addHeader(key, value) {
        this.headers[key] = value;
    }

    removeHeader(key) {
        if (this.headers[key]) {
            delete this.headers[key];
        }
    }

    getUrl() {
        return this.path;
    }

    _buildConfig() {
        return {
            url: this.getUrl(),
            method: this.method,
            contentType: this.contentType,
            headers: this.headers,
            isDevMode: false,
            processData : this.processData
        };
    }

    async send(payload) {
        let requestConfig = this._buildConfig(),
            requestId = uuid(),
            timer = new Timer(),
            interval;
        this.addHeader('Client-Request-Id', requestId);

        this.runBeforeSend(requestConfig);

        this.handleBeforeRequestSend({requestId, payload, requestConfig, timer});
        interval = setInterval(() => {
            if(timer.stopTime) {
                clearInterval(interval);
                return;
            }
            this.handleLongRunningRequestInterval({requestId, payload, requestConfig, timer});
        }, this.longRunningRequestInterval);

        // Start a timer before we send the request
        timer.start();
        return this._executeRequest(payload, requestConfig)
            .then(response => {
                return {
                    config: requestConfig,
                    payload,
                    data: response
                }
            })
            .then(response => {
                timer.stop();
                this.handleRequestCompleted({requestId, payload, requestConfig, timer});
                return response;
            })
            .then(response => {
                return this.runHooks(HOOK_NAME.BEFORE_RESPONSE, response);
            })
            .then(response => {
                response.data = this.runHooks(HOOK_NAME.TRANSFORMERS, response.data);
                return response;
            })
            .catch(error => {
                timer.stop();
                this.handleRequestCompleted({requestId, payload, requestConfig, timer});
                this.handleRequestError({requestId, error, payload, requestConfig, timer});
                return Promise.reject(this.runHooks(HOOK_NAME.ERROR_HANDLERS, error));
            });
    }

    async _executeRequest(payload, config) {
        return new Promise((resolve, reject) => {
            // in case payload is formdata contentType will be false
            const data = config.contentType ? JSON.stringify(payload) : payload;
            $.ajax({
                url: config.url,
                type: config.method,
                contentType: config.contentType,
                data,
                dataType: 'json',
                headers: config.headers,
                processData: config.processData,
                success: (...args) => {
                    resolve(...args);
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    reject({
                        jqXHR, textStatus, errorThrown,
                        payload, config,
                        instance: this,
                        constructor: getFunctionName(this.constructor)
                    });
                }
            });
        });
    }
}
ApiRequest.prototype.HOOK_NAME = HOOK_NAME;

function _customArgumentsApiRequestFactory(Class, args, ...rest) {
    return new Class(args, ...rest);
}

function _addHook(hook, hookName, context) {
    let collection = _hooks[hookName];

    if(typeof(hook) !== 'function') {
        return;
    }

    if (!collection.has(context)) {
        collection.set(context, []);
    }

    collection.get(context).push(hook);
}

module.exports = ApiRequest;